/* IWD PIR U-value engine. Original code for Insulation Warehouse Direct.
   Simplified BS EN ISO 6946:2017 (combined upper/lower limit method for bridged layers, Annex D air layers,
   Annex F wall-tie correction) and BS EN ISO 13370:2017 (ground floors) following BR 443:2019 conventions.
   Indicative only - see page disclaimer. Validation against published manufacturer tables: validation.md */
var IWDU = (function () {
  'use strict';
  var RSE = 0.04, HR0 = 5.1;                 // ISO 6946: external surface resistance; hr0 at 10 C
  var RSI = { up: 0.10, horiz: 0.13, down: 0.17 };
  var LAM = {                                 // W/mK, BR 443 / CIBSE Guide A typical values
    brickOuter: 0.77, brickSolid: 0.77, denseBlock: 1.13, mediumBlock: 0.51, lightBlock: 0.15, aerated: 0.11,
    mortar: 0.88, render: 1.0, plaster: 0.57, board: 0.21, timber: 0.13, ply: 0.13, chip: 0.13,
    screed: 1.15, concrete: 1.35, deckConcrete: 2.3, beam: 2.0, stone: 1.7
  };
  var EPS_FOIL = 0.05, EPS_HI = 0.9;          // BBA 24/7254 Table 7 declared foil emissivity; ordinary surfaces
  var R_DABS = 0.17;                          // plasterboard on dabs: unventilated ~15mm air layer (BR 443 convention)

  /* ISO 6946 Annex D: unventilated air layer, d in mm, dir up|horiz|down, e1/e2 surface emissivities */
  function airR(dmm, dir, e1, e2) {
    var d = Math.max(dmm, 1) / 1000, E = 1 / (1 / e1 + 1 / e2 - 1), hr = E * HR0, ha;
    if (dir === 'up') ha = Math.max(1.95, 0.025 / d);
    else if (dir === 'down') ha = Math.max(0.12 * Math.pow(d, -0.44), 0.025 / d);
    else ha = Math.max(1.25, 0.025 / d);
    return 1 / (ha + hr);
  }
  function L(name, dmm, lam) { return { name: name, R: dmm / 1000 / lam, d: dmm }; }
  function R(name, r) { return { name: name, R: r, d: 0 }; }
  /* Bridged group: parts [{f, name, slices:[R...]}] - every part has the same number of slices (layers) */
  function B(name, parts, dmm) { return { name: name, parts: parts, d: dmm || 0 }; }

  /* Combined method (ISO 6946 6.7.2): RT = (RT' + RT'')/2 */
  function combined(layers) {
    var homo = 0, groups = [];
    layers.forEach(function (l) { if (l.parts) groups.push(l); else homo += l.R; });
    if (!groups.length) return { RT: homo, Rup: homo, Rlo: homo };
    // upper limit: every combination of parts across groups is a parallel path
    var paths = [{ f: 1, R: homo }];
    groups.forEach(function (g) {
      var next = [];
      paths.forEach(function (p) {
        g.parts.forEach(function (pt) {
          var r = pt.slices.reduce(function (a, b) { return a + b; }, 0);
          next.push({ f: p.f * pt.f, R: p.R + r });
        });
      });
      paths = next;
    });
    var Rup = 1 / paths.reduce(function (a, p) { return a + p.f / p.R; }, 0);
    // lower limit: each slice of each group becomes an equivalent homogeneous layer
    var Rlo = homo;
    groups.forEach(function (g) {
      var n = g.parts[0].slices.length;
      for (var i = 0; i < n; i++) {
        Rlo += 1 / g.parts.reduce(function (a, pt) { return a + pt.f / pt.slices[i]; }, 0);
      }
    });
    return { RT: (Rup + Rlo) / 2, Rup: Rup, Rlo: Rlo };
  }

  /* ISO 6946 Annex F mechanical fastener correction: alpha*lamf*nf*Af/d0*(R1/RT)^2 */
  function dUfix(nf, AfMm2, lamf, d0mm, R1, RT) {
    if (!nf || !d0mm) return 0;
    return 0.8 * lamf * nf * (AfMm2 * 1e-6) / (d0mm / 1000) * Math.pow(R1 / RT, 2);
  }

  /* BS EN ISO 13370 slab on ground */
  function slabU(pa, w, lamg, Rf) {
    var B1 = 2 / pa, dt = w + lamg * (RSI.down + Rf + RSE);
    if (dt < B1) return 2 * lamg / (Math.PI * B1 + dt) * Math.log(Math.PI * B1 / dt + 1);
    return lamg / (0.457 * B1 + dt);
  }
  /* BS EN ISO 13370 suspended floor over ventilated void. Defaults per BR 443:2019 */
  var SUSP = { h: 0.225, Uw: 1.7, eps: 0.0015, v: 5, fw: 0.05 };  // BR 443 defaults: floor 225mm above ground, underfloor walls U 1.7, 0.0015 m2/m vents, 5 m/s, fw 0.05
  function suspendedU(pa, w, lamg, Uf) {
    var B1 = 2 / pa, dg = w + lamg * (RSI.down + RSE);
    var Ug = 2 * lamg / (Math.PI * B1 + dg) * Math.log(Math.PI * B1 / dg + 1);
    var Ux = 2 * SUSP.h * SUSP.Uw / B1 + 1450 * SUSP.eps * SUSP.v * SUSP.fw / B1;
    return 1 / (1 / Uf + 1 / (Ug + Ux));
  }

  var MASONRY_INNER = {                        // inner leaf 100mm blocks; mortar bridging 6.6% for light/aerated (BR 443)
    dense: { lam: LAM.denseBlock, mortar: false, label: 'Dense block' },
    medium: { lam: LAM.mediumBlock, mortar: false, label: 'Medium density block' },
    light: { lam: LAM.lightBlock, mortar: true, label: 'Lightweight block' },
    aerated: { lam: LAM.aerated, mortar: true, label: 'Aerated (aircrete) block' }
  };
  function blockLayer(kind, dmm, name) {
    var m = MASONRY_INNER[kind] || MASONRY_INNER.dense;
    if (!m.mortar) return L(name || m.label, dmm, m.lam);
    return B(name || m.label, [
      { f: 0.934, name: m.label, slices: [dmm / 1000 / m.lam] },
      { f: 0.066, name: 'Mortar', slices: [dmm / 1000 / LAM.mortar] }
    ], dmm);
  }
  function studGroup(name, depthMm, tIns, lam, ft, gapDir, gapE1) {
    // insulation (tIns) in a timber zone of depthMm; any remaining depth is an unventilated air gap next to foil
    var gap = Math.max(0, depthMm - tIns);
    var ins = { f: 1 - ft, name: 'PIR', slices: [tIns / 1000 / lam] };
    var tim = { f: ft, name: 'Timber', slices: [tIns / 1000 / LAM.timber] };
    if (gap >= 1) {
      ins.slices.push(airR(gap, gapDir, gapE1, EPS_HI));
      tim.slices.push(gap / 1000 / LAM.timber);
    }
    return B(name, [ins, tim], depthMm);
  }

  /* Main entry. o: {element, lam, t1, t2, lam2, ...}. Returns {U, layers, dU, notes, error} */
  function compute(o) {
    var lam = o.lam || 0.022, lam2 = o.lam2 || lam, t1 = +o.t1 || 0, t2 = +o.t2 || 0;
    var layers = [], notes = [], dU = 0, res, U, err = '';
    switch (o.element) {
      case 'solid-floor': {
        var screed = o.screed || 65;
        var Rf = screed / 1000 / LAM.screed + (t1 + t2) / 1000 / lam;   // slab/hardcore ignored (conservative; matches BBA 24/7254 Table 8 best)
        U = slabU(o.pa, o.wall || 0.3, o.soil || 1.5, Rf);
        layers = [L('Sand/cement screed', screed, LAM.screed), L('PIR insulation', t1 + t2, lam), R('Concrete slab (ignored)', 0)];
        notes.push('BS EN ISO 13370 slab-on-ground, B\u2032 = ' + (2 / o.pa).toFixed(1) + 'm');
        return { U: U, layers: layers, dU: 0, notes: notes };
      }
      case 'beam-block': {
        var sc = o.screed || 65;
        layers = [R('Rsi (to void)', RSI.down),
          B('150mm beam & block', [{ f: 0.88, name: 'Block infill', slices: [0.15 / LAM.denseBlock] }, { f: 0.12, name: 'Concrete beam', slices: [0.15 / LAM.beam] }], 150),
          L('PIR insulation', t1 + t2, lam), L('Sand/cement screed', sc, LAM.screed), R('Rsi', RSI.down)];
        var Uf = 1 / combined(layers).RT;
        U = suspendedU(o.pa, o.wall || 0.3, o.soil || 1.5, Uf);
        notes.push('Deck U = ' + Uf.toFixed(3) + ', BS EN ISO 13370 ventilated void');
        return { U: U, layers: layers, dU: 0, notes: notes, Uf: Uf };
      }
      case 'timber-floor': {
        var ftj = o.centres === 600 ? 0.075 : 0.11;           // BBA 24/7254 / BR 443: 47mm joists at 400mm = 11%
        if (o.joist && t1 > o.joist) err = 'Insulation is deeper than the joists.';
        layers = [R('Rsi (to void)', RSI.down), studGroup('PIR between joists', t1, t1, lam, ftj, 'down', EPS_FOIL),
          L('22mm chipboard/board deck', 22, LAM.chip), R('Rsi', RSI.down)];
        var Uf2 = 1 / combined(layers).RT;
        U = suspendedU(o.pa, o.wall || 0.3, o.soil || 1.5, Uf2);
        notes.push('Deck U = ' + Uf2.toFixed(3) + ', joists ' + Math.round(ftj * 100) + '% timber');
        return { U: U, layers: layers, dU: 0, notes: notes, error: err, Uf: Uf2 };
      }
      case 'cavity-wall': {
        var cav = o.cavity || 100, resid = cav - t1;
        if (resid < 50) err = 'Leaves less than a 50mm clear cavity - use a wider cavity or thinner board.';
        var outer = o.outer === 'block' ? [L('10mm render', 10, LAM.render), L('100mm dense block outer leaf', 100, LAM.denseBlock)] : [L('102.5mm brick outer leaf', 102.5, LAM.brickOuter)];
        layers = [R('Rse', RSE)].concat(outer, [
          R('Clear cavity ' + Math.max(resid, 0) + 'mm (low-e)', airR(Math.max(resid, 10), 'horiz', EPS_FOIL, EPS_HI)),
          L('PIR cavity board', t1, lam), blockLayer(o.inner || 'dense', 100, null)]);
        if (o.finish === 'plaster') layers.push(L('13mm plaster', 13, LAM.plaster));
        else layers.push(R('Plasterboard dabs (air gap)', R_DABS), L('12.5mm plasterboard', 12.5, LAM.board));
        layers.push(R('Rsi', RSI.horiz));
        res = combined(layers);
        // wall ties: 2.5/m2 stainless (17 W/mK), 12.5mm2 (cavity <=125mm) or 23.4mm2 - as Kingspan TW50 assumptions
        dU = dUfix(2.5, cav > 125 ? 23.4 : 12.5, 17, t1, t1 / 1000 / lam, res.RT);
        notes.push('Wall ties: \u0394U = ' + dU.toFixed(3));
        break;
      }
      case 'timber-frame': {
        var stud = o.stud || 140;
        if (t1 > stud) err = 'Insulation is deeper than the studs.';
        layers = [R('Rse', RSE), L('102.5mm brick outer leaf', 102.5, LAM.brickOuter), R('50mm cavity', airR(50, 'horiz', EPS_HI, EPS_HI)),
          L('12mm plywood sheathing', 12, LAM.ply),
          studGroup('PIR between ' + stud + 'mm studs', stud, Math.min(t1, stud), lam, 0.15, 'horiz', EPS_FOIL)];
        if (t2) layers.push(L('PIR over studs (insulated plasterboard)', t2, lam2));
        layers.push(L('12.5mm plasterboard', 12.5, LAM.board), R('Rsi', RSI.horiz));
        break;
      }
      case 'internal-wall': {
        var mas = { brick: L('215mm solid brick', 215, LAM.brickSolid), stone: L('500mm solid stone', 500, LAM.stone),
          dense: L('215mm dense block', 215, LAM.denseBlock), aerated: blockLayer('light', 215, '215mm lightweight/aircrete block') }[o.masonry || 'brick'];
        layers = [R('Rse', RSE), mas, L('15mm existing plaster', 15, LAM.plaster), R('Dabs (air gap)', R_DABS),
          L('PIR (insulated plasterboard)', t1, lam), L('12.5mm plasterboard', 12.5, LAM.board), R('Rsi', RSI.horiz)];
        break;
      }
      case 'pitched-roof': {
        var dep = o.depth || 150, ftr = o.centres === 400 ? 0.1175 : 0.078, vent = o.vent === 'vent';
        var zone = vent ? dep - 50 : dep;
        if (t1 > zone) err = vent ? 'Ventilated roofs need a 50mm air gap above the insulation - reduce the thickness or choose deeper rafters.' : 'Insulation is deeper than the rafters.';
        var ti = Math.min(t1, zone);
        if (vent) layers = [R('Rse (ventilated)', RSI.up), studGroup('PIR between rafters', ti, ti, lam, ftr, 'up', EPS_FOIL)];
        else layers = [R('Tiles/battens/membrane zone', RSI.up), studGroup('PIR between rafters', dep, ti, lam, ftr, 'up', EPS_FOIL)];
        if (t2) layers.push(L('PIR under rafters (insulated plasterboard)', t2, lam2));
        layers.push(L('12.5mm plasterboard', 12.5, LAM.board), R('Rsi', RSI.up));
        if (!vent && dep - ti > 0 && dep - ti < 13) notes.push('Air gap under the membrane is under 13mm, so little extra resistance is claimed.');
        break;
      }
      case 'flat-roof': {
        if (o.deck === 'concrete') layers = [R('Rse', RSE), L('PIR warm deck insulation', t1 + t2, lam), L('50mm screed to falls', 50, LAM.screed), L('150mm concrete deck', 150, LAM.deckConcrete),
          B('Ceiling void on 25x50 battens', [{ f: 0.917, name: 'Air', slices: [airR(25, 'up', EPS_HI, EPS_HI)] }, { f: 0.083, name: 'Batten', slices: [0.025 / LAM.timber] }], 25),
          L('12.5mm plasterboard', 12.5, LAM.board), R('Rsi', RSI.up)];
        else layers = [R('Rse', RSE), L('PIR warm deck insulation', t1 + t2, lam), L('18mm plywood deck', 18, LAM.ply),
          B('150mm joists at 600mm with air void', [{ f: 0.917, name: 'Air', slices: [airR(150, 'up', EPS_HI, EPS_HI)] }, { f: 0.083, name: 'Joist', slices: [0.15 / LAM.timber] }], 150),
          L('12.5mm plasterboard', 12.5, LAM.board), R('Rsi', RSI.up)];
        break;
      }
      default: return { U: NaN, error: 'Unknown element' };
    }
    res = res || combined(layers);
    U = 1 / res.RT + dU;
    return { U: U, Uraw: 1 / res.RT, dU: dU, RT: res.RT, Rup: res.Rup, Rlo: res.Rlo, layers: layers, notes: notes, error: err };
  }

  /* Thinnest option from a list of stocked thicknesses that meets the target */
  function thinnest(base, list1, target, t2) {
    var best = null;
    list1.slice().sort(function (a, b) { return a - b; }).some(function (t) {
      var o = {}; for (var k in base) o[k] = base[k]; o.t1 = t; o.t2 = t2 || 0;
      var r = compute(o);
      if (r.error) return false;
      if (Math.round(r.U * 100) / 100 <= target + 1e-9) { best = { t1: t, t2: t2 || 0, U: r.U }; return true; }
      return false;
    });
    return best;
  }

  return { compute: compute, thinnest: thinnest, airR: airR, combined: combined, slabU: slabU, suspendedU: suspendedU, LAM: LAM, SUSP: SUSP, RSI: RSI, EPS_FOIL: EPS_FOIL, cfg: { R_DABS: R_DABS } };
})();
if (typeof module !== 'undefined' && module.exports) module.exports = IWDU;

/* ---------------- UI: IWD PIR U-value calculator ----------------
   Live product data (variant ids, prices, availability) is rendered by Liquid into a JSON script tag,
   so prices always match the store. Board ranges/thicknesses come from the section 'part_map'. */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;
  var PHONE = '03330 908479', VAT = 0.2, MIN_ORDER = 40000, FREE_DEL = 35000;
  var ELEMENTS = {
    'solid-floor': { kind: 'floor', label: 'Solid concrete ground floor', ranges: ['gp', 'tp10', 'celotex'], second: 'same', secondLabel: 'Second layer of PIR (optional)' },
    'beam-block': { kind: 'floor', label: 'Beam and block ground floor', ranges: ['gp', 'tp10', 'celotex'], second: 'same', secondLabel: 'Second layer of PIR (optional)' },
    'timber-floor': { kind: 'floor', label: 'Suspended timber ground floor', ranges: ['gp', 'tp10', 'celotex'], second: null },
    'cavity-wall': { kind: 'wall', label: 'Cavity wall (partial fill)', ranges: ['tw50', 'eurowall'], second: null, newOnly: true },
    'timber-frame': { kind: 'wall', label: 'Timber frame wall', ranges: ['gp', 'tp10', 'celotex'], second: 'mlk', secondLabel: 'Insulated plasterboard over the studs (optional)', newOnly: true },
    'internal-wall': { kind: 'wall', label: 'Solid wall, insulated internally', ranges: ['mlk', 'pl4000'], second: null },
    'pitched-roof': { kind: 'roof', label: 'Pitched roof, between rafters', ranges: ['tp10', 'gp', 'celotex'], second: 'mlk', secondLabel: 'Insulated plasterboard under the rafters (optional)' },
    'flat-roof': { kind: 'roof', label: 'Flat roof, warm deck', ranges: ['eurodeck', 'tr26'], second: 'same', secondLabel: 'Second layer (optional)' }
  };
  var RANGES = {
    gp: 'Recticel Eurothane GP (general purpose PIR)', tp10: 'Kingspan Thermapitch TP10', tw50: 'Kingspan Thermawall TW50',
    eurowall: 'Recticel Eurowall Cavity', mlk: 'Mannok MLK insulated plasterboard', celotex: 'Celotex GA4000 / XR4000 / TB4000 (now Sopratherm)', pl4000: 'Celotex PL4000 insulated plasterboard (now Sopratherm)', eurodeck: 'Recticel Eurothane Eurodeck', tr26: 'Kingspan Thermaroof TR26'
  };
  // Approved Document L 2021 (2023 amendments) Vol 1, England: Table 4.2 (new elements) and Table 4.3 column (b)
  var TARGET_NEW = { floor: 0.18, wall: 0.18, roof: 0.15 };
  var TARGET_RENO = { floor: 0.25, wall: 0.30, roof: 0.16 };

  function money(p) { return '\u00a3' + (p / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  /* Ex/Inc VAT switch (snippets/iwd-vat-init.liquid, 2026-09-27): prices render as ex + inc pairs and CSS shows the chosen one */
  function vm(p, o) { return window.IWDVAT ? IWDVAT.dual(p, o) : money(p) + ' ex VAT'; }
  function vt(p) { return window.IWDVAT && IWDVAT.isInc() ? money(IWDVAT.inc(p)) + ' inc VAT' : money(p) + ' ex VAT'; }
  function optTxt(o, pre, p, post) { o.setAttribute('data-iwd-ex', Math.round(p)); o.setAttribute('data-iwd-pre', pre); o.setAttribute('data-iwd-post', post || ''); o.textContent = pre + vt(p) + (post || ''); }
  var PRICE_TH = '<span class="iwd-vx">Price ex VAT</span><span class="iwd-vi">Price inc VAT</span>';
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function num(v, d) { var x = parseFloat(String(v == null ? '' : v).replace(',', '.')); return isFinite(x) ? x : d; }
  function up(x) { return Math.ceil(x - 1e-9); }
  function setHTML(n, h) { if (n && n.__h !== h) { n.innerHTML = h; n.__h = h; } }  // skip identical re-renders so clicks aren't lost on blur
  function fmtT(t) { return (Math.round(t * 10) / 10) + 'mm'; }
  function readJSON(root, sel) { var out = {}; root.querySelectorAll(sel).forEach(function (el) { try { var j = JSON.parse(el.textContent); for (var k in j) out[k] = j[k]; } catch (e) {} }); return out; }
  function parseMap(txt) {
    var m = {};
    String(txt || '').split(/\r?\n/).forEach(function (l) {
      l = l.trim(); if (!l || l.charAt(0) === '#') return;
      var i = l.indexOf('='); if (i < 0) return;
      var key = l.slice(0, i).trim(), h = l.slice(i + 1).split('|')[0].trim(), d = key.indexOf('.');
      if (d < 0 || !h) return;
      var r = key.slice(0, d), t = parseFloat(key.slice(d + 1));
      if (!isFinite(t)) return;
      (m[r] = m[r] || []).push({ t: t, handle: h });
    });
    return m;
  }
  function boardInfo(title) {
    var s = /(\d{3,4})\s*(?:x|\u00d7)\s*(\d{3,4})\s*mm/i.exec(title || ''), p = /pack of\s*(\d+)/i.exec(title || '');
    var w = s ? parseInt(s[1], 10) : 2400, h = s ? parseInt(s[2], 10) : 1200;
    return { area: w * h / 1e6, size: w + ' \u00d7 ' + h + 'mm', pack: p ? parseInt(p[1], 10) : 1 };
  }

  function init(root) {
    var products = readJSON(root, '[data-iwdu-products]');
    var map = parseMap((root.querySelector('[data-iwdu-map]') || {}).textContent);
    // stock: range -> sorted [{t, handle, p, info}] of live, purchasable products only
    var stock = {};
    Object.keys(map).forEach(function (r) {
      stock[r] = map[r].map(function (e) { var p = products[e.handle]; return p && p.available && p.variant ? { t: e.t, handle: e.handle, p: p, info: boardInfo(p.title) } : null; })
        .filter(Boolean).sort(function (a, b) { return a.t - b.t; });
    });
    var f = root.querySelector('[data-iwdu-form]');
    var el = function (n) { return f.elements[n]; };
    var out = {
      u: root.querySelector('[data-iwdu-u]'), badge: root.querySelector('[data-iwdu-badge]'), rec: root.querySelector('[data-iwdu-rec]'),
      table: root.querySelector('[data-iwdu-table]'), how: root.querySelector('[data-iwdu-how]'), sweep: root.querySelector('[data-iwdu-sweep]'),
      err: root.querySelector('[data-iwdu-error]'), msg: root.querySelector('[data-iwdu-msg]'), btn: root.querySelector('[data-iwdu-add]'), ship: root.querySelector('[data-iwdu-ship]')
    };
    var state = { items: [] };

    function fillSelect(sel, opts, keep) {
      var cur = sel.value, html = '';
      opts.forEach(function (o) { html += '<option value="' + esc(o.v) + '">' + esc(o.l) + '</option>'; });
      sel.innerHTML = html;
      if (keep && opts.some(function (o) { return String(o.v) === cur; })) sel.value = cur;
    }
    function maxT(v) {  // maximum main-layer thickness allowed by the construction (mm)
      switch (v.element) {
        case 'timber-floor': return v.joist;
        case 'cavity-wall': return v.cavity - 50;
        case 'timber-frame': return v.stud;
        case 'pitched-roof': return v.vent === 'vent' ? v.depth - 50 : v.depth;
        default: return 1e9;
      }
    }
    function vals() {
      var v = {
        element: el('element').value, mode: el('mode').value, custom: num(el('custom').value, 0.18),
        pa: num(el('pa').value, 0.5), wall: num(el('wall').value, 0.3), soil: num(el('soil').value, 1.5), screed: num(el('screed').value, 65),
        joist: num(el('joist').value, 150), centres: num(el('centres').value, 400),
        outer: el('outer').value, inner: el('inner').value, finish: el('finish').value, cavity: num(el('cavity').value, 100),
        stud: num(el('stud').value, 140), masonry: el('masonry').value,
        depth: num(el('depth').value, 150), rcentres: num(el('rcentres').value, 600), vent: el('vent').value, deck: el('deck').value,
        range: el('range').value, lam: num(el('lam').value, 0.022), area: num(el('area').value, 0), waste: num(el('waste').value, 10)
      };
      v.cfg = ELEMENTS[v.element];
      return v;
    }
    function engineInput(v, t1, t2) {
      return { element: v.element, lam: v.lam, lam2: v.lam, t1: t1, t2: t2 || 0, pa: v.pa, wall: v.wall, soil: v.soil, screed: v.screed,
        joist: v.joist, centres: v.element === 'pitched-roof' ? v.rcentres : v.centres, outer: v.outer, inner: v.inner, finish: v.finish,
        cavity: v.cavity, stud: v.stud, masonry: v.masonry, depth: v.depth, vent: v.vent, deck: v.deck };
    }
    function target(v) {
      if (v.mode === 'custom') return { u: v.custom, label: 'your target of ' + v.custom.toFixed(2) };
      var k = v.cfg.kind;
      if (v.mode === 'reno' && !v.cfg.newOnly) return { u: TARGET_RENO[k], label: 'Part L renovation value of ' + TARGET_RENO[k].toFixed(2) + ' (Table 4.3)' };
      return { u: TARGET_NEW[k], label: 'Part L new-element limit of ' + TARGET_NEW[k].toFixed(2) + ' (Table 4.2)' };
    }
    function secondRange(v) { var s = v.cfg.second; return s === 'same' ? v.range : s; }

    function syncControls() {
      var v = vals(), cfg = v.cfg;
      root.querySelectorAll('[data-iwdu-for]').forEach(function (n) { n.hidden = n.getAttribute('data-iwdu-for').split(' ').indexOf(v.element) < 0; });
      root.querySelectorAll('[data-iwdu-kind]').forEach(function (n) { n.hidden = n.getAttribute('data-iwdu-kind').split(' ').indexOf(cfg.kind) < 0; });
      el('custom').closest('.iwd-field').hidden = v.mode !== 'custom';
      var changed = state.el !== v.element; state.el = v.element;
      fillSelect(el('range'), cfg.ranges.filter(function (r) { return stock[r] && stock[r].length; }).map(function (r) { return { v: r, l: RANGES[r] }; }), !changed);
      if (changed) { el('t1').value = 'auto'; if (el('t2')) el('t2').value = '0'; }
      v = vals();
      var mx = maxT(v), list = (stock[v.range] || []).filter(function (s) { return s.t <= mx; });
      fillSelect(el('t1'), [{ v: 'auto', l: 'Auto: thinnest that meets the target' }].concat(list.map(function (s) { return { v: s.t, l: fmtT(s.t) + ((v.range === 'mlk' || v.range === 'pl4000') ? ' PIR + 12.5mm board (' + fmtT(s.t + 12.5) + ')' : '') }; })), true);
      var sr = secondRange(v), sf = root.querySelector('[data-iwdu-second]');
      sf.hidden = !cfg.second || !(stock[sr] || []).length;
      if (!sf.hidden) {
        sf.querySelector('label').textContent = cfg.secondLabel;
        fillSelect(el('t2'), [{ v: 0, l: 'None' }].concat(stock[sr].map(function (s) { return { v: s.t, l: fmtT(s.t) + (sr === 'mlk' ? ' PIR + 12.5mm board (' + fmtT(s.t + 12.5) + ')' : '') }; })), true);
      }
      var reno = el('mode').querySelector('option[value="reno"]');
      reno.disabled = !!cfg.newOnly;
      if (cfg.newOnly && v.mode === 'reno') el('mode').value = 'new';
    }

    function compute() {
      syncControls();
      var v = vals(), tg = target(v), mx = maxT(v), cfg = v.cfg;
      var list = (stock[v.range] || []).filter(function (s) { return s.t <= mx; });
      var sr = secondRange(v), t2 = cfg.second && !root.querySelector('[data-iwdu-second]').hidden ? num(el('t2').value, 0) : 0;
      out.err.textContent = '';
      if (!list.length) { out.err.textContent = 'None of our ' + RANGES[v.range] + ' thicknesses fit this construction - try a deeper joist/rafter/cavity or call ' + PHONE + '.'; return; }
      if (cfg.kind === 'floor' && !(v.pa > 0.05 && v.pa <= 1.5)) { out.err.textContent = 'Enter a perimeter/area (P/A) ratio between 0.05 and 1.5.'; return; }
      var ts = list.map(function (s) { return s.t; });
      var best = IWDU.thinnest(engineInput(v, 0, 0), ts, tg.u, t2);
      var auto = el('t1').value === 'auto', t1 = auto ? (best ? best.t1 : ts[ts.length - 1]) : num(el('t1').value, ts[0]);
      var r = IWDU.compute(engineInput(v, t1, t2)), U2 = Math.round(r.U * 100) / 100, pass = U2 <= tg.u + 1e-9;
      setHTML(out.u, r.U.toFixed(2) + ' <small>W/m\u00b2K</small>');
      out.badge.className = 'iwdu-badge ' + (pass ? 'is-pass' : 'is-fail');
      setHTML(root.querySelector('[data-iwdu-mini]'), 'U = ' + r.U.toFixed(2) + ' W/m\u00b2K ' + (pass ? '\u2713 meets ' : '\u2717 misses ') + tg.u.toFixed(2) + ' \u2013 see boards &amp; price \u2193');
      out.badge.textContent = (pass ? '\u2713 Meets ' : '\u2717 Does not meet ') + tg.label;
      // recommendation: thinnest that meets target (with the current second layer), else suggest a second layer
      var rec = '', name = function (t) { return fmtT(t) + ' ' + RANGES[v.range]; };
      if (best) {
        rec = '<p><strong>Thinnest ' + RANGES[v.range] + ' that meets ' + tg.u.toFixed(2) + ':</strong> ' + fmtT(best.t1) + (t2 ? ' + ' + fmtT(t2) + (sr === 'mlk' ? ' insulated plasterboard' : ' second layer') : '') + ' (U = ' + best.U.toFixed(2) + ').';
        rec += (!auto && best.t1 !== t1) ? ' <button type="button" class="iwd-btn iwd-btn--ghost iwdu-use" data-iwdu-use="' + best.t1 + '|' + t2 + '">Use this</button>' : '';
        rec += '</p>';
      } else {
        var combo = null;
        // continuous layers under/over timbers (mlk): least extra layer first (keeps headroom); same-range layers: least total
        if (cfg.second && stock[sr]) stock[sr].some(function (s2) {
          var b = IWDU.thinnest(engineInput(v, 0, 0), ts, tg.u, s2.t);
          if (!b) return false;
          if (sr === 'mlk') { combo = b; return true; }
          if (!combo || b.t1 + b.t2 < combo.t1 + combo.t2) combo = b;
          return false;
        });
        rec = '<p><strong>No single ' + RANGES[v.range] + ' board that fits this construction reaches ' + tg.u.toFixed(2) + '.</strong> ';
        if (combo) rec += 'Thinnest combination: ' + fmtT(combo.t1) + ' + ' + fmtT(combo.t2) + ' ' + (sr === 'mlk' ? 'insulated plasterboard' : 'second layer') + ' (U = ' + combo.U.toFixed(2) + '). <button type="button" class="iwd-btn iwd-btn--ghost iwdu-use" data-iwdu-use="' + combo.t1 + '|' + combo.t2 + '">Use this</button>';
        else rec += 'Try deeper joists/rafters/cavity, a different construction, or call us on <a href="tel:03330908479">' + PHONE + '</a>.';
        rec += '</p>';
      }
      if (r.error) rec += '<p class="iwd-msg is-error">' + esc(r.error) + '</p>';
      if (cfg.newOnly && v.mode === 'reno') rec += '<p class="iwd-note">These boards go in as the wall is built, so the new-element value applies.</p>';
      setHTML(out.rec, rec);
      renderPrices(v, t1, t2, sr);
      renderHow(v, r, tg, ts, t2);
    }

    function lineFor(range, t, label, v) {
      var s = (stock[range] || []).filter(function (x) { return x.t === t; })[0];
      if (!s) return null;
      var boards = up(v.area * (1 + v.waste / 100) / s.info.area), packs = up(boards / s.info.pack);
      return { label: label, s: s, boards: boards, packs: packs, total: packs * s.p.price };
    }
    function renderPrices(v, t1, t2, sr) {
      var lines = [];
      if (v.area > 0) {
        lines.push(lineFor(v.range, t1, v.cfg.second && t2 ? 'Main layer' : 'Insulation', v));
        if (t2) lines.push(lineFor(sr, t2, sr === 'mlk' ? 'Insulated plasterboard' : 'Second layer', v));
      }
      lines = lines.filter(Boolean);
      var html = '', ex = 0, items = [];
      if (!(v.area > 0)) html = '<p class="iwd-note">Enter the area to insulate to see how many boards you need and the price.</p>';
      else {
        html += '<table class="iwd-table"><thead><tr><th>Product</th><th class="num">Boards</th><th class="num">Buy</th><th class="num">' + PRICE_TH + '</th><th class="num">Line total</th></tr></thead><tbody>';
        lines.forEach(function (l) {
          var p = l.s.p, variant = p.vtitle && !/default title/i.test(p.vtitle) ? ' (' + p.vtitle + ')' : '';
          var buy = l.s.info.pack > 1 ? l.packs + ' pack' + (l.packs === 1 ? '' : 's') + ' of ' + l.s.info.pack : l.packs + ' sheet' + (l.packs === 1 ? '' : 's');
          html += '<tr><td><span class="iwd-part">' + esc(l.label) + '</span><a class="iwd-prod" href="' + esc(p.url) + '">' + esc(p.title + variant) + '</a><span class="iwd-sub">Board ' + esc(l.s.info.size) + ', ' + (Math.round(l.s.info.area * 100) / 100) + 'm\u00b2 each</span></td>' +
            '<td class="num" data-label="Boards">' + l.boards + '</td><td class="num" data-label="Buy">' + buy + '</td><td class="num" data-label="Price">' + vm(p.price, { align: 'end' }) + (l.s.info.pack > 1 ? '<span class="iwd-sub">per pack</span>' : '<span class="iwd-sub">per sheet</span>') + '</td><td class="num" data-label="Line total">' + vm(l.total, { align: 'end' }) + '</td></tr>';
          ex += l.total; items.push({ id: p.variant, quantity: l.packs });
        });
        html += '</tbody></table>';
        var vat = Math.round(ex * VAT);
        html += '<div class="iwd-totals"><div><span>Total ex VAT</span><span data-iwdu-total-ex>' + money(ex) + '</span></div><div><span>VAT (20%)</span><span>' + money(vat) + '</span></div><div class="iwd-grand"><span>Total inc VAT</span><span data-iwdu-total-inc>' + money(ex + vat) + '</span></div></div>';
        html += '<p class="iwd-note">' + v.area.toFixed(2) + 'm\u00b2 + ' + v.waste + '% for cuts and waste. We don\u2019t deduct joists, studs or rafters - offcuts from cutting between timbers usually use that up.</p>';
      }
      setHTML(out.table, html);
      var ship = '';
      if (items.length && (v.range === 'celotex' || v.range === 'pl4000')) {
        /* Celotex (Sopratherm) ships separately: £250 ex VAT minimum, £40 ex VAT delivery under £550, free from £550 (21 Oct 2026) */
        ship = ex >= 55000 ? '\u2713 Free Celotex delivery to England (over \u00a3550 ex VAT).' : 'Celotex delivery to England: \u00a340 ex VAT under \u00a3550 ex VAT, free from \u00a3550 ex VAT.';
        if (ex < 25000) ship += ' <strong>The minimum Celotex order is \u00a3250 ex VAT<span class="iwd-vi"> (\u00a3300 inc VAT)</span></strong>. Celotex is delivered separately, so other items don\u2019t count towards it.';
      } else if (items.length) {
        ship = ex >= MIN_ORDER ? '\u2713 Free delivery to England (over our \u00a3400 ex VAT minimum order).' : 'Free delivery to England on insulation (minimum order \u00a3400 ex VAT).';
        if (ex < MIN_ORDER) ship += ' <strong>Our minimum order is \u00a3400 ex VAT<span class="iwd-vi"> (\u00a3480 inc VAT)</span></strong>, so add the rest of your materials before checkout.';
      }
      setHTML(out.ship, ship);
      state.items = items;
    }
    function renderHow(v, r, tg, ts, t2) {
      var h = '<table class="iwd-table iwdu-layers"><thead><tr><th>Layer (outside to inside)</th><th class="num">R (m\u00b2K/W)</th></tr></thead><tbody>';
      (r.layers || []).forEach(function (l) {
        var rv = l.parts ? 'bridged' : l.R.toFixed(3);
        if (l.parts) rv = l.parts.map(function (p) { return Math.round(p.f * 1000) / 10 + '% ' + p.name + ' ' + p.slices.reduce(function (a, b) { return a + b; }, 0).toFixed(2); }).join('<br>');
        h += '<tr><td>' + esc(l.name) + '</td><td class="num">' + rv + '</td></tr>';
      });
      h += '</tbody></table>';
      if (r.RT) h += '<p class="iwd-note">Upper limit R = ' + r.Rup.toFixed(2) + ', lower limit R = ' + r.Rlo.toFixed(2) + ', average R<sub>T</sub> = ' + r.RT.toFixed(2) + ' m\u00b2K/W' + (r.dU ? ', plus wall-tie correction \u0394U ' + r.dU.toFixed(3) : '') + '.</p>';
      if (r.notes && r.notes.length) h += '<p class="iwd-note">' + r.notes.map(esc).join('. ') + '.</p>';
      setHTML(out.how, h);
      var s = '<table class="iwd-table"><thead><tr><th>Thickness</th><th class="num">U-value</th><th class="num">' + tg.u.toFixed(2) + '?</th></tr></thead><tbody>';
      ts.forEach(function (t) { var x = IWDU.compute(engineInput(v, t, t2)); var ok = Math.round(x.U * 100) / 100 <= tg.u + 1e-9; s += '<tr><td>' + fmtT(t) + (t2 ? ' + ' + fmtT(t2) : '') + '</td><td class="num" data-label="U-value">' + x.U.toFixed(2) + '</td><td class="num" data-label="Meets target">' + (x.error ? 'n/a' : ok ? '\u2713' : '\u2717') + '</td></tr>'; });
      setHTML(out.sweep, s + '</tbody></table>');
    }

    // helpers: P/A and area from dimensions
    function helper(a, b, target, fn) {
      var go = function () { var x = num(f.elements[a].value, 0), y = num(f.elements[b].value, 0); if (x > 0 && y > 0) { f.elements[target].value = fn(x, y); compute(); } };
      f.elements[a].addEventListener('input', go); f.elements[b].addEventListener('input', go);
    }
    helper('perim', 'farea', 'pa', function (p, a) { return (p / a).toFixed(2); });
    helper('alen', 'awid', 'area', function (l, w) { return (l * w).toFixed(2); });
    f.addEventListener('change', compute);
    f.addEventListener('input', function (e) { if (['perim', 'farea', 'alen', 'awid'].indexOf(e.target.name) < 0) compute(); });
    f.addEventListener('submit', function (e) { e.preventDefault(); compute(); });
    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-iwdu-use]'); if (!b) return;
      var p = b.getAttribute('data-iwdu-use').split('|');
      el('t1').value = p[0]; if (+p[1] && el('t2')) el('t2').value = p[1];
      compute();
    });
    out.btn.addEventListener('click', function () {
      var items = state.items.filter(function (i) { return i.quantity > 0 && i.id; });
      if (!items.length) { out.msg.className = 'iwd-msg is-error'; out.msg.textContent = 'Enter the area to insulate first.'; return; }
      out.btn.disabled = true; out.msg.className = 'iwd-msg'; out.msg.textContent = 'Adding to your basket\u2026';
      var rt = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rt + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) { if (!res.ok) throw new Error(res.j && (res.j.description || res.j.message) || 'Could not add to basket'); window.location.href = rt + 'cart'; })
        .catch(function (e) { out.btn.disabled = false; out.msg.className = 'iwd-msg is-error'; out.msg.textContent = e.message + '. Please try again or call ' + PHONE + '.'; });
    });
    // deep-link presets e.g. ?element=cavity-wall&area=40
    try {
      var q = new URLSearchParams(window.location.search);
      ['element', 'mode', 'area', 'pa'].forEach(function (k) { if (q.get(k) && f.elements[k]) f.elements[k].value = q.get(k); });
    } catch (e) {}
    compute();
    window.IWDU_UI = { compute: compute, stock: stock, state: state };
  }
  function boot() { document.querySelectorAll('[data-iwdu-calc]').forEach(function (r) { if (r.dataset.iwduReady) return; r.dataset.iwduReady = '1'; init(r); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
